import Vue from 'vue'

import projects from '@/api/projects'
import judging from '@/api/judging'
import users from '@/api/users'
import auditorium from '@/api/auditorium'
import * as types from '../mutation-types'

const boardTypes = [
  { value: 'story', label: 'StoryBoard', description: 'STEM related videos & articles' },
  { value: 'project', label: 'ProjectBoard', description: 'Share and work on ideas together' },
  { value: 'discussion', label: 'DiscussionBoard', description: 'Ask questions' }
]

const state = {
  hasBeenModified: {},
  currentProjectID: '',
  projects: {},
  projectsByUserID: {},
  newProjectIds: [],
  likes: {},
  subscriptions: {},
  projectMembers: {},
  projectMembersOhio: {},
  projectMemberCounts: {},
  pinnedProjects: {},
  approvedProjects: {},
  declinedProjects: {},
  pendingProjects: {},
  judgingQuestions: [],
  judgingQuestionsStatus: 'idle',
  judgingQuestionsError: '',
  selectedAnswerTags: [],
  selectedCardFields: [],
  judgingTotalScore: 0,
  sortDescending: true,
  groupProjectLoadingStatus: 'idle', // idle loading loaded error
  groupProjectLoadingError: null,
  groupProjects: {},
  postCounts: {},
  usersNotCompletedQuestions: {},
  projectsByCategoryLoadingStatus: {},
  submissions: {},
  currentExplorePage: '',
  userProjects: {},
  loadingMembers: {},
  userUnansweredQuestionCount: {},
  userUnansweredPaymentQueCount: {},
  userPaymentQuestionAnswer: {},
  totalUsersUnansweredCount: {},
  showBanner: false,
  editMode: false
}

const getters = {
  editMode: (state) => {
    return state.editMode
  },
  hasBeenModified: (state) => (id) => {
    return state.hasBeenModified[id]
  },
  likedProject: (state) => (id, username) => {
    return state.likes[id] ? state.likes[id][username] : false
  },
  subscribedToProject: (state) => (id, username) => {
    return state.subscriptions[id] ? state.subscriptions[id][username] : false
  },
  userUnansweredQueCount: (state) => (userId) => {
    return state.userUnansweredQuestionCount[userId] ? state.userUnansweredQuestionCount[userId] : 0
  },
  userUnansweredPaymentQueCount: (state) => (userId) => {
    return state.userUnansweredPaymentQueCount[userId] ? state.userUnansweredPaymentQueCount[userId] : 0
  },
  userPaymentQuestionAnswer: (state) => (userId) => {
    return state.userPaymentQuestionAnswer[userId] ? state.userPaymentQuestionAnswer[userId] : null
  },
  totalUsersUnansweredCount: (state) => (projectId) => {
    return state.totalUsersUnansweredCount[projectId]
  },
  cachedProjects: (state) => (ids) => {
    const projects = []
    let missing = false
    ids.forEach((id) => {
      const project = state.projects[id]
      if (!project) {
        missing = true
      }
      projects.push(project)
    })

    if (missing) {
      return null
    }

    return projects
  },
  newProjectIds: (state) => {
    return state.newProjectIds
  },
  projectRole: (state) => (projectId, uuid) => {
    return getMember(state, projectId, uuid)?.role
  },
  project: (state) => (id) => {
    return state.projects[id] ? state.projects[id] : null
  },
  projectMembers: (state) => (id) => {
    if (state.projectMembers[id]) {
      return Object.values(state.projectMembers[id])
    }
    return []
  },
  projectMembersByRole: (state) => (id, roles) => {
    if (state.projectMembers[id]) {
      return Object.values(state.projectMembers[id]).filter(member => roles.includes(member.role))
    }
    return []
  },
  projectMembersOhio: (state) => (id) => {
    if (state.projectMembersOhio[id]) {
      return Object.values(state.projectMembersOhio[id])
    }
    return []
  },
  projectMembersCount: (state) => (id) => {
    return state.projectMemberCounts[id] || 0
  },
  linkedProjects: (state) => (id) => {
    return state.projects[id] ? state.projects[id].links || [] : []
  },
  membership: (state) => (id, uuid) => {
    return getMember(state, id, uuid)
  },
  numProjectLikes: (state) => (id) => {
    return state.projects[id] && state.projects[id].metadata ? state.projects[id].metadata.likes : 0
  },
  supportedBoardTypes: () => {
    return boardTypes.filter((type) => {
      if (Vue.prototype.$isProjects) {
        return type.value === 'project' || type.value === 'discussion'
      } else if (Vue.prototype.$isEngCom) {
        return type.value === 'story'
      } else if (Vue.prototype.$isDiscussions) {
        return type.value === 'discussion'
      }
    })
  },
  boardTypeFilters: (state) => {
    if (Vue.prototype.$isProjects) {
      return ['ProjectBoards', 'DiscussionBoards']
    }

    return []
  },
  projects: (state) => {
    return state.projects
  },
  approvedProjects: (state, getters) => {
    if (!state.approvedProjects) {
      return {}
    }
    return filterJudgingProjects(state.approvedProjects, getters.getSelectedAnswerTagsArray)
  },
  currentProjectID: (state) => {
    return state.currentProjectID
  },
  projectsCount: (state, getters, rootState) => {
    return {
      Pending: Object.keys(state.pendingProjects).length,
      Approved: Object.keys(state.approvedProjects).length,
      Declined: Object.keys(state.declinedProjects).length,
      Teams: Object.keys(rootState.judging.teams).length
    }
  },
  getJudgingProjectById: (state, rootGetters) => (projectID) => {
    switch (rootGetters.getCurrentTab) {
      case 'Pending':
        return state.pendingProjects[projectID] ?? {}
      case 'Approved':
        return state.approvedProjects[projectID] ?? {}
      case 'Declined':
        return state.declinedProjects[projectID] ?? {}
      default:
        return {}
    }
  },
  pendingProjects: (state, getters) => {
    if (!state.pendingProjects) {
      return {}
    }
    return filterJudgingProjects(state.pendingProjects, getters.getSelectedAnswerTagsArray)
  },
  declinedProjects: (state, getters) => {
    if (!state.declinedProjects) {
      return {}
    }
    return filterJudgingProjects(state.declinedProjects, getters.getSelectedAnswerTagsArray)
  },
  pinnedProjects: (state) => {
    return state.pinnedProjects
  },
  judgingQuestions: (state) => {
    return state.judgingQuestions
  },
  isProjectSubmitted: (state) => (id) => {
    return state.projects[id].submitted
  },
  getProjectByPostID: (state, getters, rootState, rootGetters) => (postID) => {
    return state.projects[rootState.post.posts[postID].projectID]
  },
  getJudgeProjectByUserID: (state) => (userID) => {
    if (userID) {
      return state.projectsByUserID[userID]
    }
    return null
  },
  getAllJudgingTags: (state, getters) => {
    if (state.judgingQuestionsStatus === 'loaded') {
      return getters.getAllJudgingOptions.map(m => m.tag)
    }
    return []
  },
  getAllJudgingOptions: (state) => {
    if (state.judgingQuestionsStatus === 'loaded') {
      return state.judgingQuestions.reduce((previous, current) => previous.concat(current.options), [])
    }
  },
  getJudgeQuestionById: (state) => (questionId) => {
    if (state.judgingQuestionsStatus === 'loaded') {
      return state.judgingQuestions.filter(m => m.id === questionId)
    }
    return null
  },
  getJudgeOptionById: (state, getters) => (optionId) => {
    if (state.judgingQuestionsStatus === 'loaded') {
      return getters.getAllJudgingOptions.filter(m => m.id === optionId)
    }
    return null
  },
  getJudgePollUserOptions: (state, getters) => (projectId) => {
    var selectedQuestionIds = getters.getSelectedCardFieldsArray
    if (!selectedQuestionIds || selectedQuestionIds.length === 0) {
      return []
    }
    const project = getters.getJudgingProjectById(projectId)
    if (project && project.pollSummary && project.pollSummary.pollUserOptions) {
      return project.pollSummary.pollUserOptions.filter(m => selectedQuestionIds.includes(m.pollID))
    }
    return []
  },
  getJudgingQuestionTotalScore: (state, getters) => {
    const numberOfTemplates = getters.getJudgingConfig.teamProjectTemplates.length ?? 1
    // For YSC we are getting questions for each templates, and multiple templates representing same question set in different languages
    // Assuming the score will be same for English and French, so the true score should be divide by the number of templates
    return state.judgingTotalScore / numberOfTemplates
  },
  getSelectedAnswerTags: (state) => {
    return state.selectedAnswerTags
  },
  getSelectedAnswerTagsArray: (state) => {
    return Object.values(state.selectedAnswerTags)
  },
  getSelectedCardFields: (state) => {
    return state.selectedCardFields
  },
  getSelectedCardFieldsArray: (state) => {
    return Object.values(state.selectedCardFields).flat(2)
  },
  getGroupProjects: (state) => {
    return state.groupProjects || {}
  },
  getPostCounts: (state) => {
    return state.postCounts || {}
  },
  usersNotCompletedQuestions: (state) => {
    return state.usersNotCompletedQuestions || {}
  },
  getProjectLoadingStatusByCategory: (state) => (category) => {
    return category in state.projectsByCategoryLoadingStatus ? state.projectsByCategoryLoadingStatus[category] : null
  },
  getSubmissions: (state) => {
    return Object.values(state.submissions)
  },
  projectHasBeenSubmitted: (state) => {
    const submissions = Object.values(state.submissions)
    return submissions?.length > 0 ? !submissions.some(obj => Object.values(obj).includes('STATUS_NOT_SUBMITTED')) : false
  },
  currentExplorePage: (state) => {
    return state.currentExplorePage || ''
  },
  userProjects: state => username => {
    const projects = []
    state.userProjects[username].forEach(projectId => {
      projects.push(state.projects[projectId])
    })
    return projects
  },
  showBanner: (state) => {
    return state.showBanner
  }
}

const getMember = function (state, id, uuid) {
  const members = state.projectMembers[id] ? state.projectMembers[id] : null
  return members ? members[uuid] : null
}

const filterJudgingProjects = function (projects, selectedAnswerTags) {
  const filteredArray = Object.values(projects).filter((project) => {
    return selectedAnswerTags.every((selectedTags) => {
      if (!project.pollSummary || !project.pollSummary.pollUserOptions) {
        return false
      }
      const optionIds = selectedTags.map((option) => option.optionIDs).flat(2)
      return project.pollSummary.pollUserOptions.some(answer => optionIds.includes(answer.optionID))
    })
  })

  filteredArray.sort(function (firstObject, secondObject) {
    var scoreA = firstObject.pollSummary.pollResponseScore
    var scoreB = secondObject.pollSummary.pollResponseScore
    if (state.sortDescending) {
      if (scoreA > scoreB) return -1
      if (scoreA < scoreB) return 1
    } else {
      if (scoreA < scoreB) return -1
      if (scoreA > scoreB) return 1
    }
  })

  return filteredArray
}

/* eslint-disable prefer-promise-reject-errors */
const actions = {
  setEditMode ({ commit }, value) {
    commit(types.SET_EDIT_MODE, value)
  },
  toggleOrder ({ commit }) {
    commit(types.TOGGLE_ORDER)
  },
  modifiedProject ({ commit }, id) {
    commit(types.HAS_MODIFIED_PROJECT, { id: id, value: true })
  },
  resetModifiedProject ({ commit }, id) {
    commit(types.HAS_MODIFIED_PROJECT, { id: id, value: false })
  },
  createProject ({ commit }, project) {
    return new Promise((resolve, reject) => {
      projects.create(project).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        if (!project.isPrivate) {
          commit(types.ADD_NEW_PROJECT_ID, responseProject.id)
        }
        resolve({ project: responseProject, existing: !!response.data.result.existing })
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProject ({ commit }, project) {
    return new Promise((resolve, reject) => {
      projects.update(project.id, project).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectCategories ({ commit }, { id, categories }) {
    return new Promise((resolve, reject) => {
      projects.updateCategories(id, categories).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectPrivate ({ commit }, { id, isPrivate }) {
    return new Promise((resolve, reject) => {
      projects.updatePrivate(id, isPrivate).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectPinned ({ commit }, { projectID, categoryID, isPinned }) {
    const id = projectID
    return new Promise((resolve, reject) => {
      projects.updatePinned(id, categoryID, isPinned).then((response) => {
        const responseProject = response.data.result.project
        isPinned && commit(types.ADD_PINNED_PROJECT, { projectID, categoryID })
        !isPinned && commit(types.REMOVE_PINNED_PROJECT, { projectID, categoryID })
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectHidden ({ commit }, { id, isHidden }) {
    return new Promise((resolve, reject) => {
      projects.updateHidden(id, isHidden).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectSubmitted ({ commit }, { id, submitted }) {
    return new Promise((resolve, reject) => {
      projects.updateSubmitted(id, submitted).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateBlueRibbon ({ commit }, { id, BlueRibbon }) {
    return new Promise((resolve, reject) => {
      projects.updateBlueRibbon(id, BlueRibbon).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateIsTemplate ({ commit }, { id, isTemplate }) {
    return new Promise((resolve, reject) => {
      projects.updateIsTemplate(id, isTemplate).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateCustomPostOrder ({ commit }, { id, customPostOrder }) {
    return new Promise((resolve, reject) => {
      projects.updateCustomPostOrder(id, customPostOrder).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectIsOnline ({ commit }, { id, isOnline }) {
    return new Promise((resolve, reject) => {
      projects.updateProjectIsOnline(id, isOnline).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  deleteProject ({ commit }, project) {
    return new Promise((resolve, reject) => {
      projects.delete(project.id).then((response) => {
        commit(types.DELETE_PROJECT, project)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  showBanner ({ commit }) {
    return new Promise((resolve, reject) => {
      projects.showBanner().then((response) => {
        const resShowBanner = response.data.result.showBanner
        commit(types.SHOW_BANNER, resShowBanner)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getProject ({ commit, state }, { id, force }) {
    return new Promise((resolve, reject) => {
      if (state.projects[id] && !force) {
        return resolve(state.projects[id])
      }
      projects.get(id).then((response) => {
        const responseProject = response.data.result.project
        if (responseProject.primaryCategoryID === 0 && responseProject.categories && responseProject.categories.length > 0) {
          responseProject.primaryCategoryID = responseProject.categories[0].id
        }
        const unansweredQueCountUserObjList = response.data.result.unansweredProjectQueCountUser
        const unansweredPaymentQueCountUser = response.data.result.unansweredPaymentQueCountUser
        const userPaymentQuestionAnswers = response.data.result.userPaymentQuestionAnswers
        if (unansweredQueCountUserObjList && unansweredPaymentQueCountUser && userPaymentQuestionAnswers) {
          commit(types.CLEAR_USER_UNANSWERED_COUNT)
        }
        if (unansweredQueCountUserObjList !== null && unansweredQueCountUserObjList !== undefined) {
          commit(types.USER_UNANSWERED_PROJECT_QUESTION_COUNT, { unansweredQueCountUserObjList: unansweredQueCountUserObjList, unansweredPaymentQueCountUser: unansweredPaymentQueCountUser, projectID: responseProject.id })
        }
        if (userPaymentQuestionAnswers !== null && userPaymentQuestionAnswers !== undefined) {
          commit(types.USER_PAYMENT_QUESTION_ANSWER, { userPaymentQuestionAnswers: userPaymentQuestionAnswers, projectID: responseProject.id })
        }
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        commit(types.DELETE_PROJECT, { id })
        reject(error)
      })
    })
  },
  getTemplateForProject ({ commit }, { id }) {
    return new Promise((resolve, reject) => {
      projects.getTemplateForProject(id).then((response) => {
        const responseProjectTemplate = response.data.result.projectTemplate
        resolve(responseProjectTemplate)
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectByArticleID ({ commit }, id) {
    return new Promise((resolve, reject) => {
      projects.getByArticleID(id).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        commit(types.DELETE_PROJECT, { id })
        reject(error)
      })
    })
  },
  getProjects ({ commit }, { limit, offset, query, sort, awardIds, exclude, filter, currentPage, filterCategories, filterTags }) {
    return new Promise((resolve, reject) => {
      projects.getAll(limit, offset, query, sort, awardIds, exclude, filter, currentPage, filterCategories, filterTags).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getFeaturedProjects ({ commit }, { limit, offset, sort, filter, currentPage }) {
    return new Promise((resolve, reject) => {
      projects.getFeaturedProjects(limit, offset, sort, filter, currentPage).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getAwardedProjects ({ commit }, { limit, offset, sort, filter }) {
    return new Promise((resolve, reject) => {
      projects.getAwardedProjects(limit, offset, sort, filter).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  premiumRows ({ commit }, { limit, offset, sort, filter }) {
    return new Promise((resolve, reject) => {
      projects.getPremiumProjects(limit, offset, sort, filter).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsByTag ({ commit }, { tag, limit, offset, sort, filter, exclude, showHidden, type, includeAllPartners }) {
    return new Promise((resolve, reject) => {
      projects.getByTag(tag, limit, offset, sort, filter, exclude, showHidden, type, includeAllPartners).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsByTemplateID ({ commit }, { templateID, limit, offset, sort, filter, exclude, showHidden, type, IncludeIsefCareerHallInventors, ExcludeIsefCareerHallInventors, alphabets, lastNames, getTemplateChildProjects }) {
    return new Promise((resolve, reject) => {
      projects.getByTemplateID(templateID, limit, offset, sort, filter, exclude, showHidden, type, IncludeIsefCareerHallInventors, ExcludeIsefCareerHallInventors, alphabets, lastNames, getTemplateChildProjects).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsByTemplateIDs ({ commit }, { templateIDs, limit, offset, sort, filter, exclude, showHidden, type, IncludeIsefCareerHallInventors }) {
    return new Promise((resolve, reject) => {
      projects.getByTemplateIDs(templateIDs, limit, offset, sort, filter, exclude, showHidden, type, IncludeIsefCareerHallInventors).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsByGroupId ({ commit }, { id, limit, offset, sort, filter }) {
    commit(types.RESET_GROUP_PROJECT)
    return new Promise((resolve, reject) => {
      commit(types.GROUP_PROJECTS_LOADING)
      projects.getProjectsByGroupId(id, limit, offset, sort, filter).then((response) => {
        const projects = response.data.result.projects
        const postCounts = response.data.result.projectPostCountList
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          project.isGroupProject = true
          commit(types.ADD_GROUP_PROJECT, project)
        })
        postCounts.forEach((countObj) => {
          commit(types.COMPLETED_MILESTONE_POST_COUNT, countObj)
        })
        commit(types.GROUP_PROJECTS_LOADED)
        resolve({ projects, total, hasMore })
      }, (error) => {
        commit(types.GROUP_PROJECTS_ERROR, error)
        reject(error)
      })
    })
  },
  getProjectPostCounts ({ commit }, projectId) {
    return new Promise((resolve, reject) => {
      projects.getProjectPostCounts(projectId).then((response) => {
        const postCounts = response.data.result.projectPostCountList
        postCounts.forEach((countObj) => {
          commit(types.COMPLETED_MILESTONE_POST_COUNT, countObj)
          commit(types.UPDATE_PROJECT_POST_MILESTONE_COUNT, { id: countObj.projectID, milestonePostCount: countObj.milestonePostCount, completedPostCount: countObj.completedPostCount })
        })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getTemplateForDiscover ({ commit }, { exploreUrl }) {
    return new Promise((resolve, reject) => {
      projects.getTemplateForDiscover(exploreUrl).then((response) => {
        const template = response.data.result.template
        resolve({ template })
      }, (error) => {
        reject(error)
      })
    })
  },
  likeProject ({ commit }, { id, username }) {
    return new Promise((resolve, reject) => {
      projects.like(id).then((response) => {
        commit(types.LIKE_PROJECT, { id })
        commit(types.LIKED_PROJECT, { id, username, liked: true })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  unlikeProject ({ commit }, { id, username }) {
    return new Promise((resolve, reject) => {
      projects.unlike(id).then((response) => {
        commit(types.UNLIKE_PROJECT, { id })
        commit(types.LIKED_PROJECT, { id, username, liked: false })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  likedProject ({ commit, state }, { id, username }) {
    return new Promise((resolve, reject) => {
      if (state.likes[id] && state.likes[id][username] !== undefined) {
        return resolve(state.likes[id][username])
      }

      if (state.likes.cached && state.likes.cached[username]) {
        return resolve(false)
      }

      projects.hasLiked(id, username).then((response) => {
        const liked = response.data.result.liked
        commit(types.LIKED_PROJECT, { id, username, liked })
        resolve(liked)
      }, (error) => {
        reject(error)
      })
    })
  },
  cacheLikedProjects ({ commit }, username) {
    if (state.likes.cached && state.likes.cached[username]) {
      return
    }

    users.getLikedProjectIDs(username).then((response) => {
      commit(types.LIKED_PROJECTS_CACHED, { username })
      response.data.result.projects.forEach((id) => {
        commit(types.LIKED_PROJECT, { id, username, liked: true })
      })
    })
  },
  projectLikedBy ({ commit }, { id, limit, offset }) {
    return new Promise((resolve, reject) => {
      projects.getLikes(id, limit, offset).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, () => {
        reject()
      })
    })
  },

  getProjectsForUser ({ commit, state }, { username, includeCategories, limit, offset, sort, filter }) {
    return new Promise((resolve, reject) => {
      users.getProjects(username, { includeCategories, limit, offset, sort, filter }).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        if (projects) {
          projects.forEach((project) => {
            if (!state.projects?.[project.id]) {
              commit(types.ADD_PROJECT, project)
            } else if (project.milestonePostCount > 0) {
              commit(types.UPDATE_PROJECT_POST_MILESTONE_COUNT, { id: project.id, milestonePostCount: project.milestonePostCount, completedPostCount: project.completedPostCount })
            }
          })
          commit(types.ADD_PROJECTS_FOR_USER, { username, projects })
        }
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getSubmissionsForPartner ({ commit }, { includeCategories, limit, offset, sort, filter, categoryFilter }) {
    return new Promise((resolve, reject) => {
      projects.getSubmissionsForPartner({ includeCategories, limit, offset, sort, filter, categoryFilter }).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getSubmissionsForPartnerCount ({ commit }, { data }) {
    const count = {}
    const params = Object.assign(data)
    return new Promise((resolve) => {
      params.filter = 'pending'
      projects.getSubmissionsForPartner(params).then((response) => {
        count.pending = response.data.result.total

        params.filter = 'declined'
        projects.getSubmissionsForPartner(params).then((response) => {
          count.declined = response.data.result.total

          params.filter = 'approved'
          projects.getSubmissionsForPartner(params).then((response) => {
            count.approved = response.data.result.total

            params.filter = 'submitted'
            projects.getSubmissionsForPartner(params).then((response) => {
              count.submitted = response.data.result.total

              params.filter = 'resubmitted'
              projects.getSubmissionsForPartner(params).then((response) => {
                count.resubmitted = response.data.result.total
                resolve(count)
              })
            })
          })
        })
      })
    })
  },
  getLastProjectForUser ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.getLastProject(username).then((response) => {
        resolve(response.data.result.project)
      }, (error) => {
        reject(error)
      })
    })
  },
  getLastDraftForUser ({ commit }, username) {
    return new Promise((resolve, reject) => {
      users.getLastDraft(username).then((response) => {
        resolve(response.data.result.project)
      }, (error) => {
        reject(error)
      })
    })
  },
  getRecommendedProjects ({ commit }, { limit, offset, query, filter, sort, exclude }) {
    return new Promise((resolve, reject) => {
      projects.getRecommended(limit, offset, query, sort, filter, exclude).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        const query = response.data.result.query
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore, query })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPopularProjects ({ commit }, { limit, offset, query, filter, sort, exclude }) {
    return new Promise((resolve, reject) => {
      projects.getPopular(limit, offset, query, filter, sort, exclude).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        const query = response.data.result.query
        if (projects && projects.length) {
          projects.forEach((project) => {
            commit(types.ADD_PROJECT, project)
          })
        }
        resolve({ projects, total, hasMore, query })
      }, (error) => {
        reject(error)
      })
    })
  },
  getPopularProjectsByCategory ({ commit }, { includeCategories, limit, offset, query, sort, filter }) {
    return new Promise((resolve, reject) => {
      projects.getPopularByCategory({ includeCategories, limit, offset, query, sort, filter }).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        if (projects && projects.length) {
          projects.forEach((project) => {
            commit(types.ADD_PROJECT, project)
          })
          resolve({ projects, total, hasMore })
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  subscribeToProject ({ commit }, { id, username }) {
    return new Promise((resolve, reject) => {
      projects.subscribe(id).then((response) => {
        commit(types.SUBSCRIBE_PROJECT, { id })
        commit(types.SUBSCRIBED_TO_PROJECT, { id, username, subscribed: true })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  unsubscribeToProject ({ commit }, { id, username }) {
    return new Promise((resolve, reject) => {
      projects.unsubscribe(id).then((response) => {
        commit(types.UNSUBSCRIBE_PROJECT, { id })
        commit(types.SUBSCRIBED_TO_PROJECT, { id, username, subscribed: false })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  setProjectSubscriptionCount ({ commit }, { id, count }) {
    commit(types.SET_PROJECT_SUBSCRIPTION_COUNT, { id, count })
  },
  isSubscribedToProject ({ commit, state }, { id, username }) {
    return new Promise((resolve, reject) => {
      if (state.subscriptions[id] && state.subscriptions[id][username] !== undefined) {
        return resolve(state.subscriptions[id][username])
      }

      projects.isSubscribed(id, username).then((response) => {
        const subscribed = response.data.result.subscribed
        commit(types.SUBSCRIBED_TO_PROJECT, { id, username, subscribed })
        resolve(subscribed)
      }, (error) => {
        reject(error)
      })
    })
  },
  cacheSubscribedProjects ({ commit }, username) {
    if (state.subscriptions.cached && state.subscriptions.cached[username]) {
      return
    }

    users.getSubscribedProjectIDs(username).then((response) => {
      commit(types.SUBSCRIBED_PROJECTS_CACHED, { username })
      response.data.result.projects.forEach((id) => {
        commit(types.SUBSCRIBED_TO_PROJECT, { id, username, subscribed: true })
      })
    })
  },
  getSubscribers ({ commit }, { id, limit, offset }) {
    return new Promise((resolve, reject) => {
      projects.getSubscribers(id, limit, offset).then((response) => {
        const users = response.data.result.users
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        resolve({ users, total, hasMore })
      }, () => {
        reject()
      })
    })
  },
  getBookmarkedProjects ({ commit }, { username, limit, offset, includeCreated, sort, filter, currentPage }) {
    return new Promise((resolve, reject) => {
      users.getSubscriptions(username, limit, offset, includeCreated, sort, filter, currentPage).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
          commit(types.SUBSCRIBED_TO_PROJECT, { id: project.id, username, subscribed: true })
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getSubscriptions ({ commit }, { username, limit, offset, includeCreated, sort, filter }) {
    return new Promise((resolve, reject) => {
      users.getSubscriptions(username, limit, offset, includeCreated, sort, filter).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
          commit(types.SUBSCRIBED_TO_PROJECT, { id: project.id, username, subscribed: true })
        })
        resolve({ projects, total, hasMore })
      }, () => {
        reject()
      })
    })
  },
  getProjectsContributedTo ({ commit }, { username, limit, offset, sort, filter }) {
    return new Promise((resolve, reject) => {
      users.getProjectContributions(username, limit, offset, sort, filter).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        if (projects) {
          projects.forEach((project) => {
            if (state.projects[project.id] && state.projects[project.id].milestonePostCount) {
              project.milestonePostCount = state.projects[project.id].milestonePostCount
              project.completedPostCount = state.projects[project.id].completedPostCount
            }
            commit(types.ADD_PROJECT, project)
          })
        }
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  clearNewProjects ({ commit }) {
    commit(types.CLEAR_NEW_PROJECT_IDS)
  },
  getMembership ({ commit, state }, { project, user }) {
    return new Promise((resolve, reject) => {
      const member = getMember(state, project.id, user.uuid)
      if (member) {
        return resolve(member)
      }

      projects.isMember(project.id, user.userName).then((response) => {
        const member = response.data.result.membership
        if (member) {
          member.user = user
          commit(types.ADD_MEMBER, { project, member })
        }
        resolve(member)
      }, (error) => {
        reject(error)
      })
    })
  },
  getMembers ({ commit, state }, { project, limit, offset }) {
    if (state.loadingMembers[project.id]) return

    return new Promise((resolve, reject) => {
      commit(types.SET_LOADING_MEMBERS, project.id)

      projects.getMembers(project.id, limit, offset).then((response) => {
        const members = response.data.result.members
        const total = response.data.result.total
        const hasMore = response.data.result.hasMore
        members.forEach(member => {
          commit(types.ADD_MEMBER, { project, member })
        })

        commit(types.UNSET_LOADING_MEMBERS, project.id)
        resolve({ members: state.projectMembers[project.id], total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getMembersCount ({ commit, state }, { id }) {
    return new Promise((resolve, reject) => {
      projects.getMembersCount(id).then((response) => {
        commit(types.ADD_MEMBERS_COUNT, { projectID: id, count: response.data.result.count })
        resolve(response.data.result.count)
      })
    })
  },
  addMember ({ commit }, { project, lookup, role }) {
    return new Promise((resolve, reject) => {
      projects.addMember(project.id, { lookup, role }).then((response) => {
        const member = response.data.result.membership
        commit(types.ADD_MEMBER, { project, member })
        resolve(member)
      }, (error) => {
        reject(error)
      })
    })
  },
  inviteMember ({ commit }, { project, lookup, role, customRole, invitedByUserRole }) {
    return new Promise((resolve, reject) => {
      projects.inviteMember(project.id, { lookup, role, customRole, invitedByUserRole }).then((response) => {
        if (response.status === 200) {
          const member = response.data.result.membership
          commit(types.ADD_MEMBER, { project, member })
          const unansweredQueCountUserObjList = response.data.result.unansweredProjectQueCountUser
          const unansweredPaymentQueCountUser = response.data.result.unansweredPaymentQueCountUser
          const userPaymentQuestionAnswers = response.data.result.userPaymentQuestionAnswers
          if (unansweredQueCountUserObjList && unansweredPaymentQueCountUser && userPaymentQuestionAnswers) {
            commit(types.CLEAR_USER_UNANSWERED_COUNT)
          }
          if (unansweredQueCountUserObjList !== null) {
            commit(types.USER_UNANSWERED_PROJECT_QUESTION_COUNT, { unansweredQueCountUserObjList: unansweredQueCountUserObjList, unansweredPaymentQueCountUser: unansweredPaymentQueCountUser, projectID: project.id })
          }
          if (userPaymentQuestionAnswers !== null && userPaymentQuestionAnswers !== undefined) {
            commit(types.USER_PAYMENT_QUESTION_ANSWER, { userPaymentQuestionAnswers: userPaymentQuestionAnswers, projectID: project.id })
          }
          resolve(member)
        } else {
          reject(response.data)
        }
      }, (error) => {
        reject(error)
      })
    })
  },
  removeMember ({ commit, state }, { project, uuid }) {
    return new Promise((resolve, reject) => {
      const member = getMember(state, project.id, uuid)
      if (!member) {
        reject()
      }
      projects.leave(project.id, member.user.userName).then((response) => {
        const isSuccess = response.data.result.success
        if (isSuccess) {
          commit(types.REMOVE_MEMBER, { project, uuid, member })
        }
        const unansweredQueCountUserObjList = response.data.result.unansweredProjectQueCountUser
        const unansweredPaymentQueCountUser = response.data.result.unansweredPaymentQueCountUser
        const userPaymentQuestionAnswers = response.data.result.userPaymentQuestionAnswers
        if (unansweredQueCountUserObjList && unansweredPaymentQueCountUser && userPaymentQuestionAnswers) {
          commit(types.CLEAR_USER_UNANSWERED_COUNT)
        }
        if (unansweredQueCountUserObjList !== null) {
          commit(types.USER_UNANSWERED_PROJECT_QUESTION_COUNT, { unansweredQueCountUserObjList: unansweredQueCountUserObjList, unansweredPaymentQueCountUser: unansweredPaymentQueCountUser, projectID: project.id })
        }
        if (userPaymentQuestionAnswers !== null && userPaymentQuestionAnswers !== undefined) {
          commit(types.USER_PAYMENT_QUESTION_ANSWER, { userPaymentQuestionAnswers: userPaymentQuestionAnswers, projectID: project.id })
        }
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  updateMember ({ commit, state }, { project, uuid, role, customRole, invitedByUserRole }) {
    return new Promise((resolve, reject) => {
      const member = getMember(state, project.id, uuid)
      if (!member) {
        reject()
      }

      projects.updateMember(project.id, member.user.userName, { role, customRole, invitedByUserRole }).then((response) => {
        if (response.status === 200) {
          const member = response.data.result.membership
          commit(types.ADD_MEMBER, { project, member })
          const unansweredQueCountUserObjList = response.data.result.unansweredProjectQueCountUser
          const unansweredPaymentQueCountUser = response.data.result.unansweredPaymentQueCountUser
          const userPaymentQuestionAnswers = response.data.result.userPaymentQuestionAnswers
          if (unansweredQueCountUserObjList && unansweredPaymentQueCountUser && userPaymentQuestionAnswers) {
            commit(types.CLEAR_USER_UNANSWERED_COUNT)
          }
          if (unansweredQueCountUserObjList !== null) {
            commit(types.USER_UNANSWERED_PROJECT_QUESTION_COUNT, { unansweredQueCountUserObjList: unansweredQueCountUserObjList, unansweredPaymentQueCountUser: unansweredPaymentQueCountUser, projectID: project.id })
          }
          if (userPaymentQuestionAnswers !== null && userPaymentQuestionAnswers !== undefined) {
            commit(types.USER_PAYMENT_QUESTION_ANSWER, { userPaymentQuestionAnswers: userPaymentQuestionAnswers, projectID: project.id })
          }
          resolve(member)
        } else {
          reject(response)
        }
      }, () => {
        reject()
      })
    })
  },
  getLinkedProjects ({ commit, state }, { project, reset }) {
    return new Promise((resolve, reject) => {
      if (reset === true) {
        commit(types.CLEAR_LINKED_PROJECTS, { id: project.id })
      }

      if (state.projects[project.id] && state.projects[project.id].links.length > 0) {
        return resolve(state.projects[project.id].links)
      }

      return projects.getLinks(project.id).then((response) => {
        const projects = response.data.result.projects
        projects.forEach((child) => {
          commit(types.ADD_LINKED_PROJECT, { id: project.id, child })
        })
        resolve(projects)
      }, () => {
        reject()
      })
    })
  },
  addLinkedProject ({ commit, state }, { parent, child }) {
    return new Promise((resolve, reject) => {
      return projects.createLink(parent.id, child.id).then(() => {
        commit(types.ADD_LINKED_PROJECT, { id: parent.id, child })
        resolve()
      }, () => {
        reject()
      })
    })
  },
  removeLinkedProject ({ commit, state }, { parent, child }) {
    return new Promise((resolve, reject) => {
      return projects.deleteLink(parent.id, child.id).then(() => {
        commit(types.REMOVE_LINKED_PROJECT, { id: parent.id, child })
        resolve()
      }, () => {
        reject()
      })
    })
  },
  copyProject ({ commit }, { template, data }) {
    return new Promise((resolve, reject) => {
      projects.copy(template, data).then((response) => {
        const project = response.data.result.project
        commit(types.ADD_PROJECT, project)
        resolve(project)
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsSimilarTo ({ commit }, { id, limit, page }) {
    return new Promise((resolve, reject) => {
      projects.getSimilar(id, limit, page).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  socketMessage ({ dispatch, state, commit }, message) {
    const payload = message.payload || {}

    if (payload.type === 'project' && payload.subType === 'post') {
      return
    }

    // new project created
    if (payload.type === 'project' && payload.action === 'add' && payload.childType === '') {
      dispatch('getProject', { id: payload.id }).then(() => {
        commit(types.ADD_NEW_PROJECT_ID, { id: payload.id })
      }).catch(() => {})
    }

    if (!state.projects[payload.id]) {
      return
    }

    // project updated
    if (payload.type === 'project' && payload.action === 'update' && payload.childType === '') {
      dispatch('getProject', { id: payload.id, force: true }).catch(() => {})
    }

    // project deleted
    if (payload.type === 'project' && payload.action === 'delete' && payload.childType === '') {
      commit(types.DELETE_PROJECT, { id: payload.id })
    }

    // new comment added
    if (payload.type === 'project' && payload.action === 'add' && payload.childType === 'comment') {
      dispatch('getProjectComments', { id: payload.id, limit: 10, socket: true })
    }

    // project link added or removed
    if (payload.type === 'project' && payload.childType === 'link' && (payload.action === 'add' || payload.action === 'delete')) {
      dispatch('getLinkedProjects', { project: { id: payload.id }, reset: true })
    }
  },
  getProjectsByCategory ({ commit }, { slug, limit, offset, sort, filter, exclude, pinned, iterator, filterTags, awardIds, skip }) {
    commit(types.CATEGORY_PROJECTS_LOADING, slug)
    return new Promise((resolve, reject) => {
      projects.getByCategory(slug, limit, offset, sort, filter, exclude, pinned, iterator, filterTags, awardIds, skip).then((response) => {
        const category = response.data.result.category
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        const requestIterator = response.data.result.iterator
        projects.forEach((project) => {
          project.isPinned && commit(types.ADD_PINNED_PROJECT, { projectID: project.id, categoryID: category.id })
          commit(types.ADD_PROJECT, project)
        })
        commit(types.CATEGORY_PROJECTS_LOADED, slug)
        resolve({ category, projects, total, hasMore, requestIterator })
      }, (error) => {
        commit(types.CATEGORY_PROJECTS_ERROR, slug)
        reject(error)
      })
    })
  },
  suggestProjectCategories ({ commit }, id) {
    return new Promise((resolve, reject) => {
      projects.suggestCategories(id).then((response) => {
        resolve(response.data.result.suggestions)
      }, (error) => {
        reject(error)
      })
    })
  },
  createStory ({ commit }, story) {
    return new Promise((resolve, reject) => {
      projects.createStory(story).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        if (!story.isPrivate) {
          commit(types.ADD_NEW_PROJECT_ID, responseProject.id)
        }
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  updateStory ({ commit }, { id, data }) {
    return new Promise((resolve, reject) => {
      projects.updateStory(id, data).then((response) => {
        const responseProject = response.data.result.project
        commit(types.ADD_PROJECT, responseProject)
        resolve(responseProject)
      }, (error) => {
        reject(error)
      })
    })
  },
  searchInProject ({ commit }, { id, searchQuery }) {
    return new Promise((resolve, reject) => {
      projects.searchInProject(id, searchQuery).then((response) => {
        resolve(response.data.result)
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsByAward ({ commit }, { awardID, limit, offset, sort, filterTags, filterCategories }) {
    return new Promise((resolve, reject) => {
      projects.getByAward(awardID, limit, offset, sort, filterTags, filterCategories).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getAllYscAwardProjects ({ commit }, { limit, offset, sort }) {
    return new Promise((resolve, reject) => {
      projects.getAllYscAwardProjects(limit, offset, sort).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getYsc2021AwardProjects ({ commit }, { limit, offset, sort }) {
    return new Promise((resolve, reject) => {
      projects.getYsc2021AwardProjects(limit, offset, sort).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getYscAwardProjects ({ commit }, { limit, offset, sort, id }) {
    return new Promise((resolve, reject) => {
      projects.getYscGrandProjects(limit, offset, sort, id).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsByYear ({ commit }, { year, limit, offset, sort }) {
    return new Promise((resolve, reject) => {
      projects.getByYear(year, limit, offset, sort).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getYscChallengeAwardProjects ({ commit }, { limit, offset, sort }) {
    return new Promise((resolve, reject) => {
      projects.getYscChallengeAwardProjects(limit, offset, sort).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectStatus ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      projects.getProjectStatus(projectID).then((response) => {
        const pending = response.data.result.Pending
        const declined = response.data.result.Declined
        const approved = response.data.result.Approved
        resolve({ pending, declined, approved })
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectStatus ({ commit }, { id, statusToAdd, statusToRemove }) {
    return new Promise((resolve, reject) => {
      projects.updateProjectStatus(id, statusToAdd, statusToRemove).then((response) => {
        const pending = response.data.result.Pending
        const declined = response.data.result.Declined
        const approved = response.data.result.Approved
        resolve({ pending, declined, approved })
      }, (error) => {
        reject(error)
      })
    })
  },
  generateQRCode ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      projects.generateQRCode(projectID).then((response) => {
        const qrLink = response.data.result.qrCodeURL
        commit(types.ADD_QRCODE, { qrLink, projectID })
        resolve(response.data.result)
      }, (error) => {
        reject(error)
      })
    })
  },
  approveOrDeclineProject ({ commit, getters }, { projectID, status, reason }) {
    return new Promise((resolve, reject) => {
      projects.approveOrDeclineProject(projectID, status, reason).then((response) => {
        const pending = response.data.result.Pending
        const declined = response.data.result.Declined
        const approved = response.data.result.Approved
        const project = getters.getJudgingProjectById(projectID)
        if (approved) {
          commit(types.ADD_APPROVEDPROJECT, project)
          commit(types.REMOVE_PENDINGPROJECT, projectID)
          commit(types.REMOVE_DECLINEDPROJECT, projectID)
        }
        if (declined) {
          commit(types.ADD_DECLINEDPROJECT, project)
          commit(types.REMOVE_APPROVEDPROJECT, projectID)
          commit(types.REMOVE_PENDINGPROJECT, projectID)
        }
        if (pending) {
          commit(types.ADD_PENDINGPROJECT, project)
          commit(types.REMOVE_APPROVEDPROJECT, projectID)
          commit(types.REMOVE_DECLINEDPROJECT, projectID)
        }
        resolve({ pending, declined, approved })
      }, (error) => {
        reject(error)
      })
    })
  },
  enterDeclineReason ({ commit }, { projectID, messageID }) {
    return new Promise((resolve, reject) => {
      projects.enterDeclineReason(projectID, messageID).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  enterDeclineComment ({ commit }, { projectID, messageBody }) {
    return new Promise((resolve, reject) => {
      projects.enterDeclineComment(projectID, messageBody).then((response) => {
        resolve(response)
      }, (error) => {
        reject(error)
      })
    })
  },
  getResourceByType ({ commit }, { limit, offset, sort, filter, query, iterator }) {
    return new Promise((resolve, reject) => {
      projects.getResourceByType(limit, offset, sort, filter, query, iterator).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        const requestIterator = response.data.result.iterator
        if (total > 0) {
          projects.forEach((project) => {
            commit(types.ADD_PROJECT, project)
          })
        }
        resolve({ projects, total, hasMore, requestIterator })
      }, (error) => {
        reject(error)
      })
    })
  },
  getTemplateCustomerQuestions ({ commit }, { templateID, excludeAnswered, context, questionGroup }) {
    return new Promise((resolve, reject) => {
      projects.getTemplateCustomerQuestions(templateID, excludeAnswered, context, questionGroup).then((response) => {
        const questions = response.data.result.questions
        const total = response.data.result.total
        resolve({ questions, total })
      }, (error) => {
        reject(error)
      })
    })
  },
  getDeclineReasons ({ commit }) {
    return new Promise((resolve, reject) => {
      projects.getDeclineReasons().then((response) => {
        const reasons = response.data.result.messages
        resolve({ reasons })
      }, (error) => {
        reject(error)
      })
    })
  },
  submitProjectTags ({ commit }, { projectID, tags }) {
    return new Promise((resolve, reject) => {
      const data = { tags }
      projects.addTags(projectID, data).then((response) => {
        const tags = response.data.result.tags
        commit(types.UPDATE_PROJECT_TAGS, { projectID, tags })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  clearProjectTags ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      const data = { projectID }
      projects.clearProjectTags(data).then((response) => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getProjectsForRegion ({ commit }, { regionID, limit, offset, sort, filter }) {
    return new Promise((resolve, reject) => {
      projects.getByRegion(regionID, limit, offset, sort, filter).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        projects.forEach((project) => {
          commit(types.ADD_PROJECT, project)
        })
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getIsefFairID ({ commit }, projectID) {
    return new Promise((resolve, reject) => {
      projects.getFairID(projectID).then((response) => {
        resolve(response.data.result.fairID)
      }, (error) => {
        reject(error)
      })
    })
  },
  getRegionForProject ({ commit }, projectID) {
    return new Promise((resolve, reject) => {
      projects.getRegionForProject(projectID).then((response) => {
        resolve(response.data.result.region)
      }, (error) => {
        reject(error)
      })
    })
  },
  getAuditoriumVideoLanguages ({ commit }, { projectID }) {
    return new Promise((resolve, reject) => {
      auditorium.getLanguagesForVideo(projectID).then((response) => {
        resolve(response.data.result)
      }, (error) => {
        reject(error)
      })
    })
  },
  trackContentView ({ commit }, { boardID, name, utmSource }) {
    return new Promise((resolve, reject) => {
      projects.trackContentView(boardID, name, utmSource).then(() => {
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  getCertificateForProject ({ commit }, projectID) {
    return new Promise((resolve, reject) => {
      projects.getCertificateForProject(projectID).then((response) => {
        resolve(response.data.result.certificate)
      }, (error) => {
        reject(error)
      })
    })
  },
  projectUpdateTemplateSettings ({ commit }, { id, data }) {
    return new Promise((resolve, reject) => {
      projects.updateTemplateSettings(id, data).then((response) => {
        commit(types.UPDATE_PROJECT_TEMPLATE_SETTINGS, { id, data })
        commit(types.UPDATE_POST_PROJECT_SETTINGS, { id, data }, { root: true })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  projectUpdateInstructions ({ commit }, { id, data }) {
    return new Promise((resolve, reject) => {
      projects.updateInstructions(id, data).then((response) => {
        commit(types.UPDATE_PROJECT_INSTRUCTIONS, { id, data })
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  updateProjectFlags ({ commit }, { id, isHidden, isEditable, submitted, isPrivate }) {
    return new Promise((resolve, reject) => {
      projects.updateProjectFlags(id, isHidden, isEditable, submitted, isPrivate).then((response) => {
        const project = response.data.result.project
        commit(types.ADD_PROJECT, project)
        resolve(project)
      }, (error) => {
        reject(error)
      })
    })
  },
  bulkInviteToProject ({ commit }, { project, emails }) {
    return new Promise((resolve, reject) => {
      projects.bulkInviteToProject(project.id, emails).then((response) => {
        const members = response.data.result.memberships
        members.forEach((member) => {
          commit(types.ADD_MEMBER, { project, member })
        })
        resolve(members)
      }, (error) => {
        reject(error)
      })
    })
  },
  getJudgesProjects ({ commit }, { templateIDs, limit, offset, sort, filter, exclude, showHidden, type }) {
    return new Promise((resolve, reject) => {
      judging.getFromJudgesProjects(templateIDs, limit, offset, sort, filter, exclude, showHidden, type).then((response) => {
        const projects = response.data.result.projects
        const total = response.data.result.total || 0
        const hasMore = response.data.result.hasMore
        const categorizedProjects = projects.reduce((acc, current) => {
          if (current.projectStatus.status === 4) {
            acc.approved.push(current)
          } else if (current.projectStatus.status === 2) {
            acc.declined.push(current)
          } else if (current.projectStatus.status === 1) {
            acc.pending.push(current)
          }
          return acc
        }, { approved: [], declined: [], pending: [] })
        commit(types.SET_APPROVED_PROJECTS, categorizedProjects.approved || [])
        commit(types.SET_DECLINED_PROJECTS, categorizedProjects.declined || [])
        commit(types.SET_PENDING_PROJECTS, categorizedProjects.pending || [])
        resolve({ projects, total, hasMore })
      }, (error) => {
        reject(error)
      })
    })
  },
  getJudgingPollQuestions ({ commit }, { projectIDs }) {
    commit(types.JUDGING_POLLS_LOADING)
    return judging.getPollByProjectId(projectIDs).then((response) => {
      commit(types.JUDGING_POLLS_LOADED, response.data.result.polls)
    }, (error) => {
      commit(types.JUDGING_POLLS_ERROR, error)
    })
  },
  updateSelectedAnswerTags ({ commit }, { selectedAnswerTags }) {
    commit(types.UPDATE_SELECTED_ANSWER_TAGS, selectedAnswerTags)
  },
  updateSelectedCardFields ({ commit }, { selectedCardFields }) {
    commit(types.UPDATE_SELECTED_CARD_FIELDS, selectedCardFields)
  },
  getSnapshotSubmissionStatus ({ commit }, { id }) {
    return new Promise((resolve, reject) => {
      commit(types.CLEAR_SUBMISSION_STATUS)
      projects.getSnapshotSubmissionStatus(id).then(response => {
        commit(types.ADD_SUBMISSION_STATUS, response.data.result.submission_status)
      }, error => reject(error))
    })
  },
  getSubmissionStatus ({ commit }, { id }) {
    return new Promise((resolve, reject) => {
      commit(types.CLEAR_SUBMISSION_STATUS)
      projects.getSubmissionStatus(id).then(response => {
        response.data.result.submission_status?.forEach(submission => commit(types.ADD_SUBMISSION_STATUS, submission))
      }, error => reject(error))
    })
  },
  checkCompletedMemberQuestions ({ commit }, { projectID, eventID }) {
    return new Promise((resolve, reject) => {
      projects.checkCompletedMemberQuestions(projectID, eventID).then((response) => {
        const customQuestionAnswerSummary = response.data.result.usersNotCompletedQuestions
        commit(types.USERS_NOT_COMPLETED_QUESTIONS, customQuestionAnswerSummary)
        resolve()
      }, (error) => {
        reject(error)
      })
    })
  },
  setCurrentActivePageValue ({ commit }, value) {
    commit(types.SET_CURRENT_EXPLORE_PAGE_VALUE, value)
  }
}

const mutations = {
  [types.SET_EDIT_MODE] (state, value) {
    state.editMode = value
  },
  [types.ADD_PROJECT] (state, project) {
    if (project.primaryCategoryID === 0 && project.categories && project.categories.length > 0) {
      project.primaryCategoryID = project.categories[0].id
    }
    project.links = state.projects[project.id] && state.projects[project.id].links ? state.projects[project.id].links : []
    delete project.isPinned
    Vue.set(state.projects, project.id, project)
    state.currentProjectID = project.id
  },
  [types.ADD_PROJECTS_FOR_USER] (state, { username, projects }) {
    Vue.set(state.userProjects, username, projects)
  },
  [types.ADD_APPROVEDPROJECT] (state, project) {
    Vue.set(state.approvedProjects, project.id, project)
  },
  [types.SET_APPROVED_PROJECTS] (state, projects) {
    state.approvedProjects = projects.reduce((acc, x) => ({ ...acc, [x.id]: x }), {})
  },
  [types.ADD_DECLINEDPROJECT] (state, project) {
    Vue.set(state.declinedProjects, project.id, project)
  },
  [types.SET_DECLINED_PROJECTS] (state, projects) {
    state.declinedProjects = projects.reduce((acc, x) => ({ ...acc, [x.id]: x }), {})
  },
  [types.ADD_PENDINGPROJECT] (state, project) {
    Vue.set(state.pendingProjects, project.id, project)
  },
  [types.SET_PENDING_PROJECTS] (state, projects) {
    state.pendingProjects = projects.reduce((acc, x) => ({ ...acc, [x.id]: x }), {})
  },
  [types.REMOVE_PENDINGPROJECT] (state, projectID) {
    if (projectID in state.pendingProjects) {
      Vue.delete(state.pendingProjects, projectID)
    }
  },
  [types.REMOVE_DECLINEDPROJECT] (state, projectID) {
    if (projectID in state.declinedProjects) {
      Vue.delete(state.declinedProjects, projectID)
    }
  },
  [types.REMOVE_APPROVEDPROJECT] (state, projectID) {
    if (projectID in state.approvedProjects) {
      Vue.delete(state.approvedProjects, projectID)
    }
  },
  [types.UPDATE_SELECTED_ANSWER_TAGS] (state, selectedAnswerTags) {
    state.selectedAnswerTags = {}
    Object.entries(selectedAnswerTags).forEach(([key, value]) => {
      Vue.set(state.selectedAnswerTags, key, value)
    })
  },
  [types.UPDATE_SELECTED_CARD_FIELDS] (state, selectedCardFields) {
    state.selectedCardFields = {}
    Object.entries(selectedCardFields).forEach(([key, value]) => {
      Vue.set(state.selectedCardFields, key, value)
    })
  },
  [types.DELETE_PROJECT] (state, project) {
    state.projects[project.id] = null
    state.currentProjectID = null
  },
  [types.LIKE_PROJECT] (state, params) {
    state.projects[params.id].metadata.likes++
  },
  [types.UNLIKE_PROJECT] (state, params) {
    if (state.projects[params.id].metadata.likes > 0) {
      state.projects[params.id].metadata.likes--
    }
  },
  [types.LIKED_PROJECT] (state, params) {
    if (!state.likes[params.id]) {
      Vue.set(state.likes, params.id, {})
    }

    Vue.set(state.likes[params.id], params.username, params.liked)
  },
  [types.LIKED_PROJECTS_CACHED] (state, params) {
    if (!state.likes.cached) {
      state.likes.cached = {}
    }

    state.likes.cached[params.username] = true
  },
  [types.SUBSCRIBE_PROJECT] (state, params) {
    state.projects[params.id].metadata.subscribers++
  },
  [types.UNSUBSCRIBE_PROJECT] (state, params) {
    if (state.projects[params.id].metadata.subscribers > 0) {
      state.projects[params.id].metadata.subscribers--
    }
  },
  [types.SUBSCRIBED_TO_PROJECT] (state, params) {
    if (!state.subscriptions[params.id]) {
      Vue.set(state.subscriptions, params.id, {})
    }

    Vue.set(state.subscriptions[params.id], params.username, params.subscribed)
  },
  [types.SET_PROJECT_SUBSCRIPTION_COUNT] (state, params) {
    // This forced overwrite allows us to assign the count to a (possibly)
    // cached project. In normal circumstances, you may want to rely on
    // SUBSCRIBE_PROJECT and UNSUBSCRIBE_PROJECT (increment/decrement by 1) instead.
    if (!state.projects[params.id]) {
      return
    }

    state.projects[params.id].metadata.subscribers = params.count
  },
  [types.SUBSCRIBED_PROJECTS_CACHED] (state, params) {
    if (!state.subscriptions.cached) {
      state.subscriptions.cached = {}
    }

    state.subscriptions.cached[params.username] = true
  },
  [types.ADD_NEW_PROJECT_ID] (state, params) {
    state.newProjectIds.push(params.id)
  },
  [types.CLEAR_NEW_PROJECT_IDS] (state) {
    state.newProjectIds = []
  },
  [types.RESET_ON_LOGOUT] (state) {
    state.newProjectIds = []
    state.likes = {}
    state.subscriptions = {}
  },
  [types.RESET_ON_FEED] (state) {
    state.projects = {}
  },
  [types.HAS_MODIFIED_PROJECT] (state, params) {
    Vue.set(state.hasBeenModified, params.id, params.value)
  },
  [types.ADD_MEMBER] (state, params) {
    if (params.project && params.project.id > 0) {
      if (!state.projectMembers[params.project.id]) {
        Vue.set(state.projectMembers, params.project.id, {})
      }
      if (process.env.APP_NAME === 'Ohiosci' && !state.projectMembersOhio[params.project.id]) {
        Vue.set(state.projectMembersOhio, params.project.id, {})
      }
      if (params.member && params.member.user) {
        Vue.set(state.projectMembers[params.project.id], params.member.user.uuid, params.member)
        if (process.env.APP_NAME === 'Ohiosci') {
          const ohioMember = params.member
          if (ohioMember.customRole !== '') {
            ohioMember.role = ohioMember.customRole
          }
          Vue.set(state.projectMembersOhio[params.project.id], params.member.user.uuid, ohioMember)
        }
      }

      const memberExists = (member) => member.user.uuid === params.member.user.uuid
      const result = state.projects[params.project.id].memberships?.some(memberExists)
      if (!result) {
        state.projects[params.project.id].memberships?.push(params.member)
      }
    }
  },
  [types.REMOVE_MEMBER] (state, params) {
    state.projects[params.project.id].memberships.forEach((member, index) => {
      if (member.user.uuid === params.uuid) {
        state.projects[params.project.id].memberships.splice(index, 1)
      }
    })
    if (params.uuid in state.projectMembers[params.project.id]) {
      delete state.projectMembers[params.project.id][params.uuid]
    }
    if (process.env.APP_NAME === 'Ohiosci') {
      if (params.uuid in state.projectMembersOhio[params.project.id]) {
        delete state.projectMembersOhio[params.project.id][params.uuid]
      }
    }
  },
  [types.CLEAR_LINKED_PROJECTS] (state, params) {
    state.projects[params.id].links = []
  },
  [types.ADD_LINKED_PROJECT] (state, params) {
    if (!state.projects[params.id].links) {
      state.projects[params.id].links = []
    }

    state.projects[params.id].links.push(params.child)
  },
  [types.REMOVE_LINKED_PROJECT] (state, params) {
    if (!state.projects[params.id].links) {
      return
    }

    state.projects[params.id].links = state.projects[params.id].links.filter((project) => {
      return project.id !== params.child.id
    })
  },
  [types.UPDATE_PROJECT_COMMENT_COUNT] (state, params) {
    if (!state.projects[params.id]) {
      return
    }

    state.projects[params.id].metadata.comments += params.count

    if (state.projects[params.id].metadata.comments < 0) {
      state.projects[params.id].metadata.comments = 0
    }
  },
  [types.UPDATE_PROJECT_POST_COUNT] (state, params) {
    if (!state.projects[params.id]) {
      return
    }

    state.projects[params.id].metadata.posts += params.count

    if (state.projects[params.id].metadata.posts < 0) {
      state.projects[params.id].metadata.posts = 0
    }
  },
  [types.UPDATE_COMPLETED_POST_COUNT] (state, params) {
    // If the postStatus changes to 'Completed' then completedPostCount +=1
    // If the postStatus changes from 'Completed' to another status, then completedPostCount -=1
    if (params.updatedPostStatus === 'Completed') {
      state.postCounts[params.projectId].completedPostCount += 1
    } else if (params.previousPostStatus === 'Completed' && params.updatedPostStatus !== 'Completed') {
      state.postCounts[params.projectId].completedPostCount -= 1
    }
  },
  [types.ADD_PINNED_PROJECT] (state, { projectID, categoryID }) {
    if (!state.pinnedProjects[categoryID]) {
      state.pinnedProjects[categoryID] = []
    }
    state.pinnedProjects[categoryID].includes(projectID) || state.pinnedProjects[categoryID].push(projectID)
  },
  [types.REMOVE_PINNED_PROJECT] (state, { projectID, categoryID }) {
    if (!state.pinnedProjects[categoryID]) {
      return
    }
    state.pinnedProjects[categoryID] = state.pinnedProjects[categoryID].filter(project => project !== projectID)
  },
  [types.UPDATE_PROJECT_TEMPLATE_SETTINGS] (state, params) {
    const id = params.id
    state.projects[id].createSection = params.data.createSection
    state.projects[id].maxSections = params.data.maxSections
    state.projects[id].reorder = params.data.reorder
    state.projects[id].displaySwoosh = params.data.displaySwoosh
    state.projects[id].swooshBackground = params.data.swooshBackground
    state.projects[id].caption = params.data.caption
  },
  [types.UPDATE_PROJECT_INSTRUCTIONS] (state, params) {
    state.projects[params.id].instructionSection = params.data.instructionSection
  },
  [types.UPDATE_PROJECT_TAGS] (state, { projectID, tags }) {
    state.projects[projectID].tags = tags
  },
  [types.JUDGING_POLLS_LOADING] (state) {
    state.judgingQuestionsStatus = 'loading'
  },
  [types.JUDGING_POLLS_LOADED] (state, payload) {
    state.judgingQuestionsStatus = 'loaded'
    state.judgingQuestions = payload
    state.judgingTotalScore = state.judgingQuestions.reduce((acc, current) => (acc += current.maxScore ?? 0), 0)
  },
  [types.JUDGING_POLLS_ERROR] (state, error) {
    state.judgingQuestionsStatus = 'error'
    state.judgingQuestionsError = error
  },
  [types.TOGGLE_ORDER] (state) {
    state.sortDescending ? state.sortDescending = false : state.sortDescending = true
  },
  [types.GROUP_PROJECTS_LOADING] (state) {
    state.groupProjectLoadingStatus = 'loading'
  },
  [types.GROUP_PROJECTS_LOADED] (state) {
    state.groupProjectLoadingStatus = 'loaded'
  },
  [types.GROUP_PROJECTS_ERROR] (state, err) {
    state.groupProjectLoadingStatus = 'error'
    state.groupProjectLoadingError = err
  },
  [types.ADD_GROUP_PROJECT] (state, project) {
    Vue.set(state.groupProjects, project.id, project)
  },
  [types.COMPLETED_MILESTONE_POST_COUNT] (state, countObj) {
    Vue.set(state.postCounts, countObj.projectID, countObj)
  },
  [types.UPDATE_PROJECT_POST_MILESTONE_COUNT] (state, params) {
    state.projects[params.id].milestonePostCount = params.milestonePostCount
    state.projects[params.id].completedPostCount = params.completedPostCount
  },
  [types.USERS_NOT_COMPLETED_QUESTIONS] (state, usersNotCompletedQuestions) {
    state.usersNotCompletedQuestions = usersNotCompletedQuestions
  },
  [types.SET_CURRENT_EXPLORE_PAGE_VALUE] (state, value) {
    state.currentExplorePage = value
  },
  [types.ADD_QRCODE] (state, params) {
    state.projects[params.projectID].qrLink = params.qrLink
  },
  [types.RESET_GROUP_PROJECT] (state) {
    state.groupProjects = {}
    Object.assign(state.groupProjects, {})
  },
  [types.CATEGORY_PROJECTS_LOADING] (state, category) {
    Vue.set(state.projectsByCategoryLoadingStatus, category, 'loading')
  },
  [types.CATEGORY_PROJECTS_LOADED] (state, category) {
    Vue.set(state.projectsByCategoryLoadingStatus, category, 'loaded')
  },
  [types.CATEGORY_PROJECTS_ERROR] (state, category) {
    Vue.set(state.projectsByCategoryLoadingStatus, category, 'error')
  },
  [types.ADD_SUBMISSION_STATUS] (state, submission) {
    Vue.set(state.submissions, submission.fair.id, submission)
  },
  [types.CLEAR_SUBMISSION_STATUS] (state) {
    state.submissions = {}
  },
  [types.SET_LOADING_MEMBERS] (state, projectID) {
    Vue.set(state.loadingMembers, projectID, true)
  },
  [types.UNSET_LOADING_MEMBERS] (state, projectID) {
    Vue.set(state.loadingMembers, projectID, false)
  },
  [types.ADD_MEMBERS_COUNT] (state, { projectID, count }) {
    Vue.set(state.projectMemberCounts, projectID, count)
  },
  [types.USER_UNANSWERED_PROJECT_QUESTION_COUNT] (state, params) {
    params.unansweredQueCountUserObjList.forEach((unansweredQueCountUserObj) => {
      Vue.set(state.userUnansweredQuestionCount, unansweredQueCountUserObj.userID, unansweredQueCountUserObj.count)
      if (!state.totalUsersUnansweredCount[params.projectID]) {
        Vue.set(state.totalUsersUnansweredCount, params.projectID, unansweredQueCountUserObj.count)
      } else {
        state.totalUsersUnansweredCount[params.projectID] = state.totalUsersUnansweredCount[params.projectID] + unansweredQueCountUserObj.count
      }
    })
    params.unansweredPaymentQueCountUser.forEach((unansweredPaymentQueCountUserObj) => {
      Vue.set(state.userUnansweredPaymentQueCount, unansweredPaymentQueCountUserObj.userID, unansweredPaymentQueCountUserObj.count)
      if (!state.totalUsersUnansweredCount[params.projectID]) {
        Vue.set(state.totalUsersUnansweredCount, params.projectID, unansweredPaymentQueCountUserObj.count)
      } else {
        state.totalUsersUnansweredCount[params.projectID] = state.totalUsersUnansweredCount[params.projectID] + unansweredPaymentQueCountUserObj.count
      }
    })
  },
  [types.USER_PAYMENT_QUESTION_ANSWER] (state, params) {
    params.userPaymentQuestionAnswers.forEach((userPaymentQuestionAnswer) => {
      Vue.set(state.userPaymentQuestionAnswer, userPaymentQuestionAnswer.userID, userPaymentQuestionAnswer.answer)
    })
  },
  [types.CLEAR_USER_UNANSWERED_COUNT] (state) {
    state.totalUsersUnansweredCount = {}
    state.userPaymentQuestionAnswer = {}
    state.userUnansweredPaymentQueCount = {}
  },
  [types.SHOW_BANNER] (state, showBanner) {
    state.showBanner = showBanner
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
